import {
  FileUpload,
  useFileUpload,
  useSnackbar
} from '@tecsinapse/cortex-react'
import { UPLOAD_ARQUIVOS } from '../data/upload-arquivos'
import { t } from '@lingui/core/macro'
import {
  ItemBiblioteca,
  UploadArquivosMutationMutation,
  UploadArquivosMutationMutationVariables
} from '@/core/types/graphql'
import { Metadata } from '@/modules/biblioteca-virtual/types/types'
import { upload } from '@/modules/biblioteca-virtual/utils/functions'
import { maxSize } from '../utils/constants'
import { usePortalAxios } from '@tecsinapse-org/portal-menu-kit'
import { getGraphQLErrorMessage } from '@/core/utils/graphql-error'
import { useState } from 'react'
import { request } from '../../../core/utils/request'
import { useMutation, useQueryClient } from '@tanstack/react-query'

const snackDuration = 10000

export const useUploaderDialog = ({
  pastaAtual,
  isFolder
}: {
  pastaAtual: ItemBiblioteca
  isFolder: boolean
}) => {
  const [hasError, setHasError] = useState(false)
  const client = useQueryClient()

  const uploadArquivosMutation = useMutation({
    mutationKey: ['uploadArquivos'],
    mutationFn: async ({
      variables
    }: {
      variables: UploadArquivosMutationMutationVariables
    }) =>
      await request<
        UploadArquivosMutationMutation,
        UploadArquivosMutationMutationVariables
      >(UPLOAD_ARQUIVOS, { variables }),
    onSuccess: async () => {
      await client.refetchQueries({ queryKey: ['ultimosModificados'] })
      await client.refetchQueries({ queryKey: ['itemsPorPasta'] })
    }
  })
  const { instance } = usePortalAxios()
  const { snackbar } = useSnackbar()

  const onAccept = async (_accepted: FileUpload<Metadata>[]) => {
    const idPastaPai = pastaAtual.id

    const promises = (_accepted ?? []).map(async file => {
      try {
        const r = await upload(instance, file)
        return {
          ...file,
          status: 'success',
          metadata: {
            id: String(r.id)
          }
        } as FileUpload<Metadata>
      } catch (e) {
        snackbar.show('error', (e as Error).message, {
          duration: snackDuration
        })
        setHasError(true)
        throw new Error((e as Error).message)
      }
    })

    const files = await Promise.all(promises)
    if (files) {
      try {
        await uploadArquivosMutation.mutateAsync({
          variables: {
            arquivos: files
              .filter(it => it.status === 'success')
              .map(file => {
                return {
                  idArquivo: String(file.metadata?.id),
                  nome: file.file.name,
                  relativePath: file.file.relativePath,
                  idPastaPai
                }
              })
          }
        })

        snackbar.show('success', t`Arquivo(s) carregados com sucesso!`, {
          duration: snackDuration
        })

        if (files.some(file => file.status === 'error')) {
          snackbar.show(
            'error',
            t`Alguns arquivos não foram carregados corretamente.`
          )
        }

        files
          .filter(it => it.status === 'error')
          .forEach(file => {
            snackbar.show(
              'error',
              t`${file.file.name} não foi carregado corretamente.`,
              {
                duration: snackDuration
              }
            )
          })

        setHasError(false)
        onClose()
      } catch (error) {
        setHasError(true)
        console.error(error)
        const message =
          getGraphQLErrorMessage(error) ??
          t`Não foi possível enviar os arquivos :(`
        snackbar.show('error', message, {
          duration: snackDuration
        })
        throw new Error(message)
      }
    }

    return files
  }

  const { dropzoneProps, onClose, onOpen, open } = useFileUpload({
    allowMultiple: true,
    onAccept,
    maxSize,
    preventDuplicates: true,
    onDuplicate: () => {
      snackbar.show('warning', t`Arquivo duplicado. Não foi possível enviar.`, {
        duration: snackDuration
      })
    },
    isFolder,
    onOpenManager: () => {
      snackbar.show(
        'warning',
        t`Não feche nem recarregue a aba do navegador durante o upload`,
        { duration: snackDuration }
      )
    },
    onFileRejected: rejections => {
      rejections.forEach(({ file, errors }) => {
        if (file.size > maxSize) {
          snackbar.show('error', t`Arquivo maior que o limite`, {
            duration: snackDuration
          })
        } else {
          errors.forEach(error =>
            snackbar.show('error', error.message, { duration: snackDuration })
          )
        }
      })
    },
    uploadProgressText: t`Upload(s) em progresso`,
    uploadResultText: hasError
      ? t`O upload falhou, verifique os arquivos`
      : t`Upload(s) concluído(s) com sucesso!`,
    noClick: isFolder
  })

  return {
    open,
    onOpen,
    onClose,
    dropzoneProps
  }
}
